import { useState } from 'react';
import { Modal } from './modal';

const pad = (n: number) => String(n).padStart(2, '0');
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export function validMonth(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value) && Number(value.slice(0, 4)) >= 1000;
}
/** Reject calendar overflow and nonexistent local times rather than normalizing them. */
export function localExpiry(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const [year = 0, month = 0, day = 0, hour = -1, minute = -1] = value.split(/[-T:]/).map(Number);
  if (!validMonth(value.slice(0, 7))) return null;
  const date = new Date(year, month - 1, day, hour, minute);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  )
    return null;
  return date.toISOString();
}
export function DateField({
  id,
  label,
  value,
  mode,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  mode: 'month' | 'datetime';
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(() => new Date());
  const [draft, setDraft] = useState(value);
  const [time, setTime] = useState('23:59');
  const year = page.getFullYear(),
    month = page.getMonth();
  const prefix = `${year}-${pad(month + 1)}`;
  const valid = mode === 'month' ? validMonth(draft) : !!localExpiry(draft);
  function launch() {
    setDraft(value);
    setTime(value.slice(11) || '23:59');
    setPage(
      validMonth(value.slice(0, 7))
        ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, 1)
        : new Date(),
    );
    setOpen(true);
  }
  return (
    <div className="field date-field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <button id={id} type="button" className="date-trigger" disabled={disabled} onClick={launch}>
        <span>
          {value
            ? mode === 'month'
              ? `${months[Number(value.slice(5, 7)) - 1]} ${value.slice(0, 4)}`
              : new Date(value).toLocaleString([], {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
            : 'No expiry'}
        </span>
        <span aria-hidden="true">▦</span>
      </button>
      {open && (
        <Modal label={label} onClose={() => setOpen(false)}>
          <div className="date-picker">
            <h3>{label}</h3>
            <div className="calendar-heading">
              <button
                type="button"
                className="btn"
                aria-label={mode === 'month' ? 'Previous year' : 'Previous month'}
                disabled={year <= 1000}
                onClick={() =>
                  setPage(
                    new Date(
                      year - (mode === 'month' ? 1 : 0),
                      month - (mode === 'month' ? 0 : 1),
                      1,
                    ),
                  )
                }
              >
                ←
              </button>
              <strong aria-live="polite">
                {mode === 'month' ? year : `${months[month]} ${year}`}
              </strong>
              <button
                type="button"
                className="btn"
                aria-label={mode === 'month' ? 'Next year' : 'Next month'}
                disabled={year >= 9999}
                onClick={() =>
                  setPage(
                    new Date(
                      year + (mode === 'month' ? 1 : 0),
                      month + (mode === 'month' ? 0 : 1),
                      1,
                    ),
                  )
                }
              >
                →
              </button>
            </div>
            <div className={mode === 'month' ? 'calendar-months' : 'calendar-days'}>
              {mode === 'month' ? (
                months.map((name, i) => (
                  <button
                    type="button"
                    key={name}
                    aria-pressed={draft === `${year}-${pad(i + 1)}`}
                    onClick={() => setDraft(`${year}-${pad(i + 1)}`)}
                  >
                    {name.slice(0, 3)}
                  </button>
                ))
              ) : (
                <>
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                  {Array.from({ length: new Date(year, month, 1).getDay() }, (_, i) => (
                    <span key={`blank-${i}`} />
                  ))}
                  {Array.from({ length: new Date(year, month + 1, 0).getDate() }, (_, i) => {
                    const day = `${prefix}-${pad(i + 1)}`;
                    return (
                      <button
                        type="button"
                        key={day}
                        aria-label={day}
                        aria-pressed={draft.slice(0, 10) === day}
                        onClick={() => setDraft(`${day}T${time}`)}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
            <div className="field">
              <label className="label" htmlFor={`${id}-typed`}>
                {mode === 'month' ? 'Month · YYYY-MM' : 'Date · YYYY-MM-DD'}
              </label>
              <input
                id={`${id}-typed`}
                type="text"
                value={mode === 'month' ? draft : draft.split('T')[0]}
                placeholder={mode === 'month' ? '2026-09' : '2026-09-30'}
                onChange={(e) =>
                  setDraft(mode === 'month' ? e.target.value : `${e.target.value}T${time}`)
                }
              />
            </div>
            {mode === 'datetime' && (
              <div className="field">
                <label className="label" htmlFor={`${id}-time`}>
                  Local time · 24-hour HH:mm
                </label>
                <input
                  id={`${id}-time`}
                  type="text"
                  value={time}
                  placeholder="23:59"
                  onChange={(e) => {
                    setTime(e.target.value);
                    setDraft(`${draft.split('T')[0]}T${e.target.value}`);
                  }}
                />
              </div>
            )}
            {draft && !valid && (
              <p role="alert">
                Enter a valid {mode === 'month' ? 'month' : 'local date and time'} in the format
                shown.
              </p>
            )}
            <div className="form-actions">
              <button
                type="button"
                className="btn go"
                disabled={!valid}
                onClick={() => {
                  onChange(draft);
                  setOpen(false);
                }}
              >
                Apply
              </button>
              {mode === 'datetime' && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  No expiry
                </button>
              )}
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
